'use client';

import { c as _c } from "react/compiler-runtime";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '@lib/api/api-response';
import { useImportQueueRail } from '@lib/import-queue-ui/context/import-queue-rail-context';
import { scheduleImportQueueRowRetry } from '@lib/import-queue-ui/utils/schedule-import-queue-row-retry';
import { fileSizeLimitMessage } from '@lib/insights/constants/file-size-limit';
import { LARGE_INSIGHT_IMPORT_FILES, MAX_INSIGHT_FOLDER_TREE_PATHS, MAX_INSIGHT_UPLOAD_FILES } from '@lib/insights/constants/insight-upload';
import { insightQueryKeys } from '@lib/insights/hooks/insight-query-keys';
import type { InsightFolderTreeResult } from '@lib/insights/types/insight-folder-tree-result';
import { uploadInsightDocument as uploadInsightDocumentClient } from '@lib/insights/utils/upload-insight-document';
import { useQueryClient } from '@lib/query/client';
import { MAX_UPLOAD_SIZE_BYTES } from '@lib/shared/constants/max-upload-size';
import { mapWithConcurrency } from '@lib/shared/utils/map-with-concurrency';
import { spaceTreeQueryKeys } from '@lib/space-tree/constants/space-tree-query-keys';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import type { FileWithPath, FolderTreeNode } from '../../types/folder-tree-node';
import { countTreeFiles, parseFolderTree } from '../../utils/parse-folder-tree';
import { LargeInsightImportDialog, type LargeInsightImportRequest } from '../components/large-insight-import-dialog';
import type { InsightsBulkImportQueueItem } from '../types/insights-bulk-import-queue-item';
import { InsightsBulkImportContext, type InsightsBulkImportContextValue } from './insights-bulk-import-context';

/**
 * Uploads in flight at once. Each is a begin, a direct PUT to
 * Storage and a commit, so most of a file's time is network
 * latency rather than bandwidth — a few more in flight keeps
 * the connection busy without crowding the browser's
 * per-host connection pool.
 */
const UPLOAD_CONCURRENCY = 6;

/**
 * Minimum gap between list refreshes while an import runs.
 * Uploaded documents should appear as they land rather than
 * after the last file, but a full refetch of the list, data
 * table and trees per file would swamp a long import.
 */
const LIST_REFRESH_INTERVAL_MS = 10_000;

/**
 * Floor between the rail refreshes an import fires as uploads
 * finish. Small files upload in well under a second, so
 * refreshing per file polled the queue several times a second
 * and stacked statement timeouts on `insight_items`
 * (PORTAL-1RV). The rail's own poll already runs at 5s; this
 * only has to surface new rows sooner.
 */
const RAIL_REFRESH_MIN_INTERVAL_MS = 2500;
interface InsightsBulkImportProviderProps {
  children: ReactNode;
}
interface FileUploadPlan {
  file: File;
  relativePath: string;
  /** Folder path the file sits in; null for loose files. */
  folderPath: string | null;
}
function parentFolderPath(path: string): string | null {
  const slash = path.lastIndexOf('/');
  return slash === -1 ? null : path.slice(0, slash);
}

/**
 * The created folder a file at `folderPath` lands in: its own
 * folder, or the nearest ancestor that was created when its
 * own failed, or the import target.
 */
function resolveParentId(folderPath: string | null, folderIds: Record<string, string>, targetFolderId: string | null): string | null {
  for (let path = folderPath; path !== null; path = parentFolderPath(path)) {
    const id = folderIds[path];
    if (id) return id;
  }
  return targetFolderId;
}
async function createInsightFolderTree(paths: string[], parentId: string | null): Promise<InsightFolderTreeResult> {
  const response = await fetch('/api/protected/insights/folders/tree', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      paths,
      parentId
    })
  });
  if (!response.ok) {
    throw new ApiError('Failed to create folders', response.status);
  }
  return response.json();
}
function collectFolderPaths(nodes: FolderTreeNode[]): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    if (node.name !== '__root__') paths.push(node.path);
    paths.push(...collectFolderPaths(node.children));
  }
  return paths;
}
function tooManyFilesToast() {
  toast.error('Too many files selected', {
    description: `Upload up to ${MAX_INSIGHT_UPLOAD_FILES.toLocaleString()} files at a time.`
  });
}

/**
 * Returns the id of the row it created, or null.
 *
 * The caller has to record it: the rail de-duplicates client
 * items against server rows by `documentId`, so an item that
 * never learns its id renders twice — once from the client
 * with a Retry button that re-runs the identical failing
 * upload, and once from the server as "Re-upload required".
 */
async function registerFailedDocument(file: File, parentSpaceNodeId: string | null, errorMessage: string): Promise<string | null> {
  const response = await fetch('/api/protected/insights/documents/register-failed', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      fileName: file.name,
      fileSizeBytes: file.size,
      // The route caps this at 500; a long Storage error must
      // not cost the file its failed row.
      errorMessage: errorMessage.slice(0, 500),
      parentSpaceNodeId
    })
  });
  if (!response.ok) return null;
  const document = (await response.json()) as {
    id?: string;
  };
  return document.id ?? null;
}
function relativePathForTreeFile(node: FolderTreeNode, file: File): string {
  if (node.name === '__root__') return file.name;
  return `${node.path}/${file.name}`;
}
export function InsightsBulkImportProvider(t0) {
  const $ = _c(53);
  const {
    children
  } = t0;
  const queryClient = useQueryClient();
  const {
    setOpen: setImportQueueRailOpen
  } = useImportQueueRail();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [queue, setQueue] = useState(t1);
  const [activeUploadCount, setActiveUploadCount] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const cancelledClientIdsRef = useRef(t2);
  const lastRailRefreshAtRef = useRef(0);
  const [largeImportRequest, setLargeImportRequest] = useState(null);
  const largeImportResolveRef = useRef(null);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (fileCount, folderCount) => {
      if (fileCount <= LARGE_INSIGHT_IMPORT_FILES) {
        return Promise.resolve(true);
      }
      return new Promise(resolve => {
        largeImportResolveRef.current?.(false);
        largeImportResolveRef.current = resolve;
        setLargeImportRequest({
          fileCount,
          folderCount
        });
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const confirmLargeImport = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = confirmed => {
      largeImportResolveRef.current?.(confirmed);
      largeImportResolveRef.current = null;
      setLargeImportRequest(null);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const resolveLargeImport = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (clientId, updates) => {
      setQueue(prev => prev.map(row => row.clientId === clientId ? {
        ...row,
        ...updates
      } : row));
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const patchItem = t5;
  let t6;
  if ($[5] !== queryClient) {
    t6 = async () => {
      await Promise.all([insightQueryKeys.documents(), insightQueryKeys.dataTableAll(), insightQueryKeys.quickSearchAll(), insightQueryKeys.folders(), spaceTreeQueryKeys.all].map(queryKey => queryClient.invalidateQueries({
        queryKey
      }, {
        cancelRefetch: false
      })));
    };
    $[5] = queryClient;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const invalidateListQueries = t6;
  let t7;
  if ($[7] !== invalidateListQueries || $[8] !== queryClient) {
    t7 = async () => {
      await Promise.all([invalidateListQueries(), queryClient.invalidateQueries({
        queryKey: insightQueryKeys.processingQueue()
      }, {
        cancelRefetch: false
      })]);
    };
    $[7] = invalidateListQueries;
    $[8] = queryClient;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const invalidateQueries = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = async item => {
      if (cancelledClientIdsRef.current.has(item.clientId)) {
        setQueue(prev_0 => prev_0.filter(row_0 => row_0.clientId !== item.clientId));
        return;
      }
      patchItem(item.clientId, {
        status: "uploading"
      });
      if (item.file.size > MAX_UPLOAD_SIZE_BYTES) {
        const message = fileSizeLimitMessage(item.file.size);
        let registeredId = null;
        try {
          registeredId = await registerFailedDocument(item.file, item.parentSpaceNodeId, message);
        } catch {}
        patchItem(item.clientId, {
          status: "failed",
          errorMessage: message,
          ...(registeredId ? {
            documentId: registeredId
          } : {})
        });
        return;
      }
      setActiveUploadCount(_temp);
      ;
      try {
        const doc = await uploadInsightDocumentClient({
          file: item.file,
          parentSpaceNodeId: item.parentSpaceNodeId
        }).finally(() => setActiveUploadCount(_temp2));
        patchItem(item.clientId, {
          status: "done",
          documentId: doc.id,
          uploadedAt: new Date().toISOString(),
          errorMessage: undefined
        });
      } catch (t9) {
        const e = t9;
        const message_0 = e instanceof Error ? e.message : "Upload failed";
        let registeredId_0 = null;
        try {
          registeredId_0 = await registerFailedDocument(item.file, item.parentSpaceNodeId, message_0);
        } catch {}
        patchItem(item.clientId, {
          status: "failed",
          errorMessage: message_0,
          ...(registeredId_0 ? {
            documentId: registeredId_0
          } : {})
        });
      }
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const executeImportItem = t8;
  const lastListRefreshRef = useRef(0);
  let t9;
  if ($[11] !== invalidateListQueries) {
    t9 = () => {
      const now = Date.now();
      if (now - lastListRefreshRef.current < LIST_REFRESH_INTERVAL_MS) {
        return;
      }
      lastListRefreshRef.current = now;
      invalidateListQueries();
    };
    $[11] = invalidateListQueries;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const refreshListDuringImport = t9;
  let t10;
  if ($[13] !== queryClient) {
    t10 = () => {
      const now_0 = Date.now();
      if (now_0 - lastRailRefreshAtRef.current < RAIL_REFRESH_MIN_INTERVAL_MS) {
        return;
      }
      lastRailRefreshAtRef.current = now_0;
      queryClient.invalidateQueries({
        queryKey: insightQueryKeys.processingQueue()
      }, {
        cancelRefetch: false
      });
    };
    $[13] = queryClient;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const refreshRailDuringImport = t10;
  let t11;
  if ($[15] !== refreshListDuringImport || $[16] !== refreshRailDuringImport) {
    t11 = async items => {
      lastListRefreshRef.current = 0;
      await mapWithConcurrency(items, UPLOAD_CONCURRENCY, async item_0 => {
        await executeImportItem(item_0);
        refreshRailDuringImport();
        refreshListDuringImport();
      });
    };
    $[15] = refreshListDuringImport;
    $[16] = refreshRailDuringImport;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const runUploads = t11;
  let t12;
  if ($[18] !== setImportQueueRailOpen) {
    t12 = newItems => {
      setImportQueueRailOpen(true);
      flushSync(() => {
        setQueue(prev_1 => [...prev_1, ...newItems]);
      });
    };
    $[18] = setImportQueueRailOpen;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const showInRail = t12;
  let t13;
  if ($[20] !== invalidateQueries || $[21] !== runUploads) {
    t13 = async items_0 => {
      await runUploads(items_0);
      await invalidateQueries();
    };
    $[20] = invalidateQueries;
    $[21] = runUploads;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const runAndRefresh = t13;
  let t14;
  if ($[23] !== runAndRefresh || $[24] !== showInRail) {
    t14 = async (files, targetFolderId) => {
      if (files.length === 0) {
        return;
      }
      if (files.length > MAX_INSIGHT_UPLOAD_FILES) {
        tooManyFilesToast();
        return;
      }
      if (!(await confirmLargeImport(files.length, 0))) {
        return;
      }
      const newItems_0 = files.map(file => ({
        clientId: uuidv4(),
        file,
        status: "queued",
        relativePath: file.name,
        parentSpaceNodeId: targetFolderId
      }));
      showInRail(newItems_0);
      await runAndRefresh(newItems_0);
    };
    $[23] = runAndRefresh;
    $[24] = showInRail;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  const startFileImport = t14;
  let t15;
  if ($[26] !== queryClient || $[27] !== runAndRefresh || $[28] !== showInRail) {
    t15 = async (files_0, targetFolderId_0) => {
      const tree = parseFolderTree(files_0);
      const fileCount_0 = countTreeFiles(tree);
      if (fileCount_0 === 0) {
        toast.error("No supported files found", {
          description: "Only PDF files are supported."
        });
        return;
      }
      if (fileCount_0 > MAX_INSIGHT_UPLOAD_FILES) {
        tooManyFilesToast();
        return;
      }
      const folderPaths = collectFolderPaths(tree);
      if (folderPaths.length > MAX_INSIGHT_FOLDER_TREE_PATHS) {
        toast.error("Too many folders", {
          description: `This import has more than ${MAX_INSIGHT_FOLDER_TREE_PATHS.toLocaleString()} folders. Split it and try again.`
        });
        return;
      }
      if (!(await confirmLargeImport(fileCount_0, folderPaths.length))) {
        return;
      }
      const plans = [];
      function collect(nodes) {
        for (const node of nodes) {
          for (const file_0 of node.files) {
            plans.push({
              file: file_0,
              relativePath: relativePathForTreeFile(node, file_0),
              folderPath: node.name === "__root__" ? null : node.path
            });
          }
          collect(node.children);
        }
      }
      collect(tree);
      const queuedItems = plans.map(plan => ({
        clientId: uuidv4(),
        file: plan.file,
        status: "queued",
        relativePath: plan.relativePath,
        parentSpaceNodeId: targetFolderId_0
      }));
      showInRail(queuedItems);
      let folderIds = {};
      if (folderPaths.length > 0) {
        try {
          const result = await createInsightFolderTree(folderPaths, targetFolderId_0);
          folderIds = result.folderIds;
        } catch {
          const ids = new Set(queuedItems.map(_temp3));
          setQueue(prev_2 => prev_2.filter(row_1 => !ids.has(row_1.clientId)));
          toast.error("Could not create folders", {
            description: "Nothing was uploaded. Try the import again."
          });
          return;
        }
        queryClient.invalidateQueries({
          queryKey: insightQueryKeys.folders()
        });
        queryClient.invalidateQueries({
          queryKey: spaceTreeQueryKeys.all
        });
      }
      const items_1 = queuedItems.map((item_2, index) => ({
        ...item_2,
        parentSpaceNodeId: resolveParentId(plans[index].folderPath, folderIds, targetFolderId_0)
      }));
      const parentByClientId = new Map(items_1.map(_temp4));
      setQueue(prev_3 => prev_3.map(row_2 => parentByClientId.has(row_2.clientId) ? {
        ...row_2,
        parentSpaceNodeId: parentByClientId.get(row_2.clientId) ?? null
      } : row_2));
      await runAndRefresh(items_1);
    };
    $[26] = queryClient;
    $[27] = runAndRefresh;
    $[28] = showInRail;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const startFolderImport = t15;
  let t16;
  if ($[30] !== invalidateQueries || $[31] !== queue) {
    t16 = clientId_0 => {
      scheduleImportQueueRowRetry({
        clientId: clientId_0,
        queue,
        setQueue,
        toQueuedRow: _temp5,
        execute: refreshed => executeImportItem(refreshed).then(() => invalidateQueries())
      });
    };
    $[30] = invalidateQueries;
    $[31] = queue;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const retryItem = t16;
  let t17;
  if ($[33] !== queue) {
    t17 = queue.some(_temp6);
    $[33] = queue;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const hasActiveWork = t17;
  const needsTab = hasActiveWork || activeUploadCount > 0;
  let t18;
  let t19;
  if ($[35] !== needsTab) {
    t18 = () => {
      if (!needsTab) {
        return;
      }
      const onBeforeUnload = _temp7;
      window.addEventListener("beforeunload", onBeforeUnload);
      return () => window.removeEventListener("beforeunload", onBeforeUnload);
    };
    t19 = [needsTab];
    $[35] = needsTab;
    $[36] = t18;
    $[37] = t19;
  } else {
    t18 = $[36];
    t19 = $[37];
  }
  useEffect(t18, t19);
  let t20;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setQueue(prev_4 => {
        for (const row_5 of prev_4) {
          cancelledClientIdsRef.current.add(row_5.clientId);
        }
        return [];
      });
    };
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const clearAll = t20;
  let t21;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = clientId_1 => cancelledClientIdsRef.current.has(clientId_1);
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  const isClientImportCancelled = t21;
  let t22;
  if ($[40] !== activeUploadCount || $[41] !== hasActiveWork || $[42] !== queue || $[43] !== retryItem || $[44] !== startFileImport || $[45] !== startFolderImport) {
    t22 = {
      queue,
      startFileImport,
      startFolderImport,
      retryItem,
      hasActiveWork,
      activeUploadCount,
      clearAll,
      isClientImportCancelled
    };
    $[40] = activeUploadCount;
    $[41] = hasActiveWork;
    $[42] = queue;
    $[43] = retryItem;
    $[44] = startFileImport;
    $[45] = startFolderImport;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  const value = t22;
  let t23;
  if ($[47] !== largeImportRequest) {
    t23 = <LargeInsightImportDialog request={largeImportRequest} onResolve={resolveLargeImport} data-sentry-element="LargeInsightImportDialog" data-sentry-source-file="insights-bulk-import-provider.tsx" />;
    $[47] = largeImportRequest;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== children || $[50] !== t23 || $[51] !== value) {
    t24 = <InsightsBulkImportContext.Provider value={value} data-sentry-element="InsightsBulkImportContext.Provider" data-sentry-component="InsightsBulkImportProvider" data-sentry-source-file="insights-bulk-import-provider.tsx">{children}{t23}</InsightsBulkImportContext.Provider>;
    $[49] = children;
    $[50] = t23;
    $[51] = value;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
}
function _temp7(event) {
  event.preventDefault();
}
function _temp6(row_4) {
  return row_4.status === "queued" || row_4.status === "uploading";
}
function _temp5(row_3) {
  return {
    ...row_3,
    status: "queued" as const,
    errorMessage: undefined
  };
}
function _temp4(item_3) {
  return [item_3.clientId, item_3.parentSpaceNodeId];
}
function _temp3(item_1) {
  return item_1.clientId;
}
function _temp2(n_0) {
  return n_0 - 1;
}
function _temp(n) {
  return n + 1;
}